import React, { useState, useEffect, useCallback } from "react";
import {
  Users, Plus, Trash2, X, Check, Loader2, Shield, ShieldCheck,
  UserCheck, Eye, Download, AlertTriangle, Search, RefreshCw,
  ChevronDown, ChevronUp, FileText, Bell, CheckCircle, XCircle,
  ToggleLeft, ToggleRight, Lock, Smartphone
} from "lucide-react";
import { logger } from "../utils/logger";
import TeamHubExtras from "./TeamHubExtras";
import ManageAccessModal from "./ManageAccessModal";
import DocViewerModal from "./team/DocViewerModal";
import { AddStaffForm } from "./team/AddStaffForm";
import { Settings as SettingsIcon } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = {
  manager: { label: "Manager", desc: "Bookings + CRM + WhatsApp", color: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  ops_head: { label: "Ops Head", desc: "CRM, vendors, calendar, order tracking", color: "bg-indigo-100 text-indigo-700", dot: "bg-indigo-500" },
  hr_head: { label: "HR Head", desc: "People ops, policies, payroll review", color: "bg-fuchsia-100 text-fuchsia-700", dot: "bg-fuchsia-500" },
  sales_manager: { label: "Sales Manager", desc: "All leads, team performance, assignment", color: "bg-pink-100 text-pink-700", dot: "bg-pink-500" },
  sales_executive: { label: "Sales Executive", desc: "Assigned leads, pricing, follow-ups", color: "bg-orange-100 text-orange-700", dot: "bg-orange-500" },
  ops_executive: { label: "Ops Executive", desc: "Event execution, setup requirements", color: "bg-teal-100 text-teal-700", dot: "bg-teal-500" },
  cfo: { label: "CFO / Finance", desc: "Revenue, transactions, financial reports", color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  security_manager: { label: "Security / IT", desc: "Audit logs, health & security dashboard", color: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
};

const DEPARTMENTS = [
  { value: "general", label: "General" },
  { value: "operations", label: "Operations" },
  { value: "hr", label: "HR" },
  { value: "security", label: "Security" },
  { value: "finance", label: "Finance" },
  { value: "crm", label: "CRM" },
  { value: "sales", label: "Sales" },
  { value: "marketing", label: "Marketing" },
  { value: "management", label: "Management" },
  { value: "training", label: "Training" },
];

const STATUS_CONFIG = {
  pending_verification: { label: "Onboarding", color: "bg-yellow-100 text-yellow-700", icon: AlertTriangle },
  pending_approval: { label: "Awaiting Approval", color: "bg-orange-100 text-orange-700", icon: Bell },
  approved: { label: "Approved", color: "bg-green-100 text-green-700", icon: CheckCircle },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: XCircle },
};

// Document Viewer Modal
// Main Team Hub Component
const TeamHubTab = ({ auth, authAxios }) => {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState("all"); // all, active, pending, inactive
  const [roleFilter, setRoleFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [form, setForm] = useState({ name: "", username: "", password: "", role: "manager", department: "general", phone: "", email: "", login_mobile: "", reporting_manager: "" });
  const [addError, setAddError] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [expandedManager, setExpandedManager] = useState(null);
  const [docViewManager, setDocViewManager] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [accessManager, setAccessManager] = useState(null);
  const [availableDesignations, setAvailableDesignations] = useState([]);
  const [managerCandidates, setManagerCandidates] = useState([]);
  const [candidatesRole, setCandidatesRole] = useState("");
  const [headcountHint, setHeadcountHint] = useState("");
  const [mobileDraft, setMobileDraft] = useState({});
  const [mobileSaving, setMobileSaving] = useState(null);

  const fetchDesignations = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/designations`);
      setAvailableDesignations(r.data?.designations || []);
    } catch { /* silent */ }
  }, [authAxios]);

  const fetchManagers = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API_URL}/api/admin/managers`);
      setManagers(res.data);
    } catch (err) { logger.error(err); }
    setLoading(false);
  }, [authAxios]);

  const fetchPendingCount = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API_URL}/api/admin/pending-approvals-count`);
      setPendingCount(res.data.total_pending || 0);
    } catch (e) { console.debug('[TeamHub] Pending count fetch failed:', e.message); }
  }, [authAxios]);

  useEffect(() => {
    fetchManagers();
    fetchPendingCount();
    fetchDesignations();
  }, [fetchManagers, fetchPendingCount, fetchDesignations]);

  // Smart suggestions: when role changes, auto-fill department + fetch filtered reporting manager list
  useEffect(() => {
    let alive = true;
    if (!form.role) return;
    (async () => {
      try {
        const [dept, elig] = await Promise.all([
          authAxios.get(`${API_URL}/api/hr/role-dept/${encodeURIComponent(form.role)}`),
          authAxios.get(`${API_URL}/api/admin/managers/eligible-managers`, { params: { role: form.role } }),
        ]);
        if (!alive) return;
        const suggestedDept = (dept.data?.department || "General").toLowerCase();
        const normDept = (DEPARTMENTS.find((d) => d.value === suggestedDept)?.value) || "general";
        setForm((f) => (f.department === "general" || !f.department ? { ...f, department: normDept } : f));
        setManagerCandidates(elig.data?.candidates || []);
        setCandidatesRole(elig.data?.reports_to_role || "");
      } catch { /* silent */ }
      // Headcount warning for custom designations
      const d = availableDesignations.find((x) => x.key === form.role);
      if (d && d.headcount_target != null && d.headcount_current >= d.headcount_target) {
        setHeadcountHint(`Team is full — ${d.headcount_current}/${d.headcount_target} ${d.label}s already active.`);
      } else {
        setHeadcountHint("");
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.role, availableDesignations]);

  const addManager = async () => {
    if (!form.name || !form.username || !form.password) {
      setAddError("All fields are required");
      return;
    }
    setAddError("");
    setAddLoading(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/managers`, form);
      setForm({ name: "", username: "", password: "", role: "manager", department: "general", phone: "", email: "", login_mobile: "", reporting_manager: "" });
      setShowAdd(false);
      await fetchManagers();
      await fetchPendingCount();
    } catch (err) {
      setAddError(err.response?.data?.detail || "Failed to add user");
    }
    setAddLoading(false);
  };

  const saveLoginMobile = async (m) => {
    const uname = m.username;
    setMobileSaving(uname);
    try {
      const val = (mobileDraft[uname] ?? m.login_mobile ?? "").trim();
      await authAxios.put(`${API_URL}/api/admin/managers/${uname}/login-mobile`, { login_mobile: val });
      setMobileDraft((d) => { const next = { ...d }; delete next[uname]; return next; });
      await fetchManagers();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to save login mobile");
    }
    setMobileSaving(null);
  };

  const deactivateManager = async (uname) => {
    if (!window.confirm(`Deactivate @${uname}? They will lose access immediately but will stay in your team list. Use Archive if they've left the company.`)) return;
    try {
      await authAxios.post(`${API_URL}/api/admin/managers/${uname}/deactivate`);
      await fetchManagers();
    } catch (e) { alert(e?.response?.data?.detail || "Failed to deactivate"); }
  };

  const archiveManager = async (m) => {
    const ok = window.confirm(
      `Archive "${m.name}" (@${m.username})?\n\n` +
      `• They will be removed from the active team list\n` +
      `• All their sessions will end immediately\n` +
      `• You have 90 DAYS to restore them if they rejoin\n` +
      `• After 90 days the record is permanently purged\n\n` +
      `Click OK to archive.`
    );
    if (!ok) return;
    try {
      await authAxios.delete(`${API_URL}/api/admin/managers/${m.username}`);
      await fetchManagers();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to archive");
    }
  };

  const changeRole = async (m) => {
    const newRole = window.prompt(
      `Change role for ${m.name} (@${m.username}).\n\nCurrent role: ${m.role}\n\nType the role key (e.g. sales_manager, tech_team, ops_manager):`,
      m.role || ""
    );
    if (!newRole || newRole === m.role) return;
    try {
      await authAxios.put(`${API_URL}/api/admin/managers/${m.username}/role`, { role: newRole.trim().toLowerCase() });
      await fetchManagers();
      alert(`Role changed to "${newRole}". They will see new access on next login.`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to change role");
    }
  };

  const reactivateManager = async (uname) => {
    try {
      await authAxios.post(`${API_URL}/api/admin/managers/${uname}/reactivate`);
      await fetchManagers();
    } catch { alert("Failed to reactivate"); }
  };

  const approveManager = async (uname) => {
    if (!window.confirm(`Approve @${uname}? They will get full dashboard access.`)) return;
    try {
      await authAxios.post(`${API_URL}/api/admin/managers/${uname}/approve`);
      await fetchManagers();
      await fetchPendingCount();
    } catch { alert("Failed to approve"); }
  };

  const rejectManager = async () => {
    if (!rejectModal) return;
    try {
      await authAxios.post(`${API_URL}/api/admin/managers/${rejectModal}/reject`, { reason: rejectReason || "Documents not satisfactory" });
      setRejectModal(null);
      setRejectReason("");
      await fetchManagers();
      await fetchPendingCount();
    } catch { alert("Failed to reject"); }
  };

  // Filtered managers
  const filtered = managers.filter(m => {
    if (filter === "active" && (!m.active || m.verification_status !== "approved")) return false;
    if (filter === "pending" && m.verification_status !== "pending_approval" && m.verification_status !== "pending_verification") return false;
    if (filter === "inactive" && m.active) return false;
    if (roleFilter !== "all" && m.role !== roleFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return m.name?.toLowerCase().includes(q) || m.username?.toLowerCase().includes(q);
    }
    return true;
  });

  const pendingApprovals = managers.filter(m => m.verification_status === "pending_approval" && m.active);

  if (loading) return <div className="text-center py-12"><Loader2 className="animate-spin mx-auto text-[#E83E8C]" size={32} /></div>;

  return (
    <div className="space-y-6" data-testid="team-hub-tab">
      {/* Super-admin manual + AI process guides + designations */}
      <TeamHubExtras authAxios={authAxios} />

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total Staff", value: managers.length, color: "bg-blue-500" },
          { label: "Active", value: managers.filter(m => m.active && m.verification_status === "approved").length, color: "bg-green-500" },
          { label: "Pending Approval", value: pendingApprovals.length, color: "bg-orange-500", notify: true },
          { label: "Onboarding", value: managers.filter(m => m.verification_status === "pending_verification" && m.active).length, color: "bg-yellow-500" },
          { label: "Inactive", value: managers.filter(m => !m.active).length, color: "bg-gray-400" },
        ].map((stat, i) => (
          <div key={stat.label} className="bg-white rounded-2xl p-4 shadow-sm relative">
            <div className={`w-2 h-2 rounded-full ${stat.color} absolute top-3 right-3`} />
            {stat.notify && stat.value > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold animate-pulse" data-testid="pending-approval-badge">{stat.value}</span>
            )}
            <p className="text-2xl font-bold text-[#1E1B4B]">{stat.value}</p>
            <p className="text-xs text-[#94a3b8]">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Pending Approvals Alert */}
      {pendingApprovals.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4" data-testid="pending-approvals-alert">
          <div className="flex items-center gap-3 mb-3">
            <Bell size={20} className="text-orange-600" />
            <h3 className="font-bold text-[#1E1B4B]">{pendingApprovals.length} Pending Approval{pendingApprovals.length > 1 ? "s" : ""}</h3>
          </div>
          <div className="space-y-2">
            {pendingApprovals.map((m) => (
              <div key={m.username} className="flex items-center gap-3 p-3 bg-white rounded-xl">
                <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-sm">{m.name?.[0]?.toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-[#1E1B4B] truncate">{m.name}</p>
                  <p className="text-xs text-[#94a3b8]">@{m.username} &middot; {ROLES[m.role]?.label || m.role}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDocViewManager(m)}
                    className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200 flex items-center gap-1"
                    data-testid={`review-docs-${m.username}`}
                  >
                    <Eye size={14} /> Review
                  </button>
                  <button
                    onClick={() => approveManager(m.username)}
                    className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 flex items-center gap-1"
                    data-testid={`approve-${m.username}`}
                  >
                    <Check size={14} /> Approve
                  </button>
                  <button
                    onClick={() => { setRejectModal(m.username); setRejectReason(""); }}
                    className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 flex items-center gap-1"
                    data-testid={`reject-${m.username}`}
                  >
                    <X size={14} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add New + Filters */}
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-bold text-[#1E1B4B] flex items-center gap-2">
            <Users size={20} className="text-[#E83E8C]" /> All Staff Members
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={() => { fetchManagers(); fetchPendingCount(); }} className="p-2 hover:bg-gray-100 rounded-lg" title="Refresh">
              <RefreshCw size={16} className="text-[#94a3b8]" />
            </button>
            <button
              onClick={() => setShowAdd(!showAdd)}
              className="px-4 py-2 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white rounded-xl text-sm font-medium flex items-center gap-2 hover:opacity-90"
              data-testid="add-staff-btn"
            >
              <Plus size={14} /> Add Staff
            </button>
          </div>
        </div>

        {/* Add Form */}
        {showAdd && (
          <AddStaffForm
            form={form}
            setForm={setForm}
            ROLES={ROLES}
            DEPARTMENTS={DEPARTMENTS}
            availableDesignations={availableDesignations}
            managerCandidates={managerCandidates}
            candidatesRole={candidatesRole}
            headcountHint={headcountHint}
            addError={addError}
            addLoading={addLoading}
            addManager={addManager}
            onCancel={() => { setShowAdd(false); setAddError(""); }}
          />
        )}

        {/* Search & Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name or username..."
              className="w-full pl-9 pr-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none"
              data-testid="staff-search"
            />
          </div>
          <select value={filter} onChange={e => setFilter(e.target.value)} className="px-3 py-2 rounded-xl border text-sm" data-testid="status-filter">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="inactive">Inactive</option>
          </select>
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="px-3 py-2 rounded-xl border text-sm" data-testid="role-filter">
            <option value="all">All Roles</option>
            {Object.entries(ROLES).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>

        {/* Current Admin */}
        <div className="flex items-center gap-4 p-3 rounded-xl border border-[#E83E8C]/20 bg-gradient-to-r from-[#FFFAF5] to-white mb-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] flex items-center justify-center text-white font-bold text-sm">A</div>
          <div className="flex-1">
            <p className="font-semibold text-sm text-[#1E1B4B]">{auth.username}</p>
            <p className="text-xs text-[#94a3b8]">Primary Admin &middot; Full Access</p>
          </div>
          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">Admin</span>
        </div>

        {/* Staff List */}
        {filtered.length === 0 ? (
          <p className="text-sm text-[#94a3b8] text-center py-8">No staff members found matching your filters.</p>
        ) : (
          <div className="space-y-2">
            {filtered.map((m) => {
              const roleConfig = ROLES[m.role] || { label: m.role, color: "bg-gray-100 text-gray-700", dot: "bg-gray-400" };
              const statusConfig = STATUS_CONFIG[m.verification_status] || STATUS_CONFIG.pending_verification;
              const StatusIcon = statusConfig.icon;
              const isExpanded = expandedManager === m.username;

              return (
                <div key={m.username} className={`rounded-xl border ${!m.active ? "opacity-60 border-gray-200 bg-gray-50" : "border-gray-100 hover:border-[#E83E8C]/30 bg-white"} transition-all`}>
                  <div className="flex items-center gap-3 p-3 cursor-pointer" data-testid={`team-member-row-${m.username}`} onClick={() => setExpandedManager(isExpanded ? null : m.username)}>
                    <div className={`w-10 h-10 rounded-full ${roleConfig.dot} flex items-center justify-center text-white font-bold text-sm`}>
                      {m.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-[#1E1B4B] truncate">{m.name}</p>
                        {!m.active && <span className="text-xs text-red-500 font-medium">(deactivated)</span>}
                      </div>
                      <p className="text-xs text-[#94a3b8]">@{m.username} {m.department && m.department !== "general" ? `· ${m.department}` : ""}</p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${roleConfig.color} hidden sm:inline`}>{roleConfig.label}</span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.color} flex items-center gap-1`}>
                      <StatusIcon size={12} /> <span className="hidden sm:inline">{statusConfig.label}</span>
                    </span>
                    {isExpanded ? <ChevronUp size={16} className="text-[#94a3b8]" /> : <ChevronDown size={16} className="text-[#94a3b8]" />}
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div><p className="text-[#94a3b8] text-xs">Role</p><p className="font-medium">{roleConfig.label}</p></div>
                        <div><p className="text-[#94a3b8] text-xs">Department</p><p className="font-medium capitalize">{m.department || "General"}</p></div>
                        <div><p className="text-[#94a3b8] text-xs">Created</p><p className="font-medium">{m.created_at ? new Date(m.created_at).toLocaleDateString() : "-"}</p></div>
                        <div><p className="text-[#94a3b8] text-xs">Status</p><p className="font-medium">{statusConfig.label}</p></div>
                      </div>

                      {/* Document Summary */}
                      <div className="flex items-center gap-4 text-sm">
                        <p className="text-[#94a3b8] text-xs">Docs:</p>
                        {["aadhaar", "pan", "photo"].map(dt => (
                          <span key={dt} className={`flex items-center gap-1 text-xs ${m.documents?.[dt] ? "text-green-600" : "text-red-400"}`}>
                            {m.documents?.[dt] ? <CheckCircle size={12} /> : <XCircle size={12} />}
                            {dt === "aadhaar" ? "Aadhaar" : dt === "pan" ? "PAN" : "Photo"}
                          </span>
                        ))}
                        <span className={`flex items-center gap-1 text-xs ${m.rules_accepted ? "text-green-600" : "text-red-400"}`}>
                          {m.rules_accepted ? <CheckCircle size={12} /> : <XCircle size={12} />} Rules
                        </span>
                        <span className={`flex items-center gap-1 text-xs ${m.nda_accepted ? "text-green-600" : "text-red-400"}`}>
                          {m.nda_accepted ? <CheckCircle size={12} /> : <XCircle size={12} />} NDA
                        </span>
                      </div>

                      {m.rejection_reason && (
                        <div className="bg-red-50 p-3 rounded-lg text-sm text-red-700">
                          <p className="font-medium text-xs">Rejection Reason:</p>
                          <p>{m.rejection_reason}</p>
                        </div>
                      )}

                      {/* Mobile OTP Login */}
                      <div className="pt-2 border-t border-gray-100">
                        <p className="text-[#94a3b8] text-xs mb-1.5 flex items-center gap-1.5">
                          <Smartphone size={12} /> Mobile OTP Login
                          {m.login_mobile ? (
                            m.login_mobile_verified
                              ? <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-semibold" data-testid={`login-mobile-status-${m.username}`}>Verified</span>
                              : <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold" data-testid={`login-mobile-status-${m.username}`}>Pending verification</span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-semibold" data-testid={`login-mobile-status-${m.username}`}>Not set</span>
                          )}
                        </p>
                        <div className="flex gap-2 max-w-sm" onClick={(e) => e.stopPropagation()}>
                          <input
                            value={mobileDraft[m.username] ?? m.login_mobile ?? ""}
                            onChange={(e) => setMobileDraft({ ...mobileDraft, [m.username]: e.target.value })}
                            placeholder="+91XXXXXXXXXX"
                            className="flex-1 px-3 py-1.5 rounded-lg border text-xs focus:border-[#E83E8C] outline-none"
                            data-testid={`login-mobile-input-${m.username}`}
                          />
                          <button
                            onClick={(e) => { e.stopPropagation(); saveLoginMobile(m); }}
                            disabled={mobileSaving === m.username}
                            className="px-3 py-1.5 bg-[#1E1B4B] text-white rounded-lg text-xs font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
                            data-testid={`save-login-mobile-${m.username}`}
                          >
                            {mobileSaving === m.username ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
                          </button>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-0.5">Staff can sign in with this number via OTP. Leave blank to disable.</p>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 pt-1">
                        {Object.keys(m.documents || {}).length > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDocViewManager(m); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200"
                            data-testid={`view-docs-${m.username}`}
                          >
                            <FileText size={14} /> View Documents
                          </button>
                        )}
                        {m.verification_status === "pending_approval" && m.active && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); approveManager(m.username); }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600"
                              data-testid={`approve-btn-${m.username}`}
                            >
                              <Check size={14} /> Approve
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setRejectModal(m.username); setRejectReason(""); }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200"
                            >
                              <X size={14} /> Reject
                            </button>
                          </>
                        )}
                        {m.active ? (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); changeRole(m); }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-100"
                              data-testid={`change-role-${m.username}`}
                              title="Fix a wrongly-assigned role"
                            >
                              <UserCheck size={14} /> Change Role
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setAccessManager(m); }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium hover:bg-indigo-100"
                              data-testid={`manage-access-${m.username}`}
                              title="Override which dashboard tabs this employee can see"
                            >
                              <SettingsIcon size={14} /> Manage Access
                              {Array.isArray(m.allowed_tabs_override) && m.allowed_tabs_override.length > 0 && (
                                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-indigo-500 text-white text-[9px] font-bold">custom</span>
                              )}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); deactivateManager(m.username); }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-100"
                              data-testid={`deactivate-${m.username}`}
                              title="Temporarily disable login (keep in team list)"
                            >
                              <ToggleRight size={14} /> Deactivate
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); archiveManager(m); }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100"
                              data-testid={`archive-${m.username}`}
                              title="Remove from team (90-day recovery window)"
                            >
                              <Trash2 size={14} /> Archive
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); reactivateManager(m.username); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-600 rounded-lg text-xs font-medium hover:bg-green-100"
                            data-testid={`reactivate-${m.username}`}
                          >
                            <ToggleLeft size={14} /> Reactivate
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-[#94a3b8] mt-4">Ops Team members (Ops Manager & Executives) are managed separately in the "Ops Team" tab and login via /ops portal.</p>
      </div>

      {/* Document Viewer Modal */}
      <DocViewerModal
        isOpen={!!docViewManager}
        onClose={() => setDocViewManager(null)}
        manager={docViewManager || {}}
        authAxios={authAxios}
      />

      {/* Manage Access Modal — per-employee tab override */}
      <ManageAccessModal
        open={!!accessManager}
        manager={accessManager}
        authAxios={authAxios}
        onClose={() => setAccessManager(null)}
        onSaved={fetchManagers}
      />

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4" data-testid="reject-modal">
            <h3 className="text-lg font-bold text-[#1E1B4B] mb-3">Reject @{rejectModal}</h3>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (optional)..."
              className="w-full px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none resize-none h-24"
              data-testid="reject-reason-input"
            />
            <div className="flex gap-2 mt-4">
              <button onClick={rejectManager} className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600" data-testid="confirm-reject-btn">Reject</button>
              <button onClick={() => setRejectModal(null)} className="px-4 py-2 bg-gray-100 text-gray-500 rounded-xl text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamHubTab;
